import type { CountryResponse } from 'api/Countries/types'
import { fetchStore } from 'api/Store/actions'
import { fetchStores } from 'api/Stores/actions'
import type { StoresType } from 'api/Stores/types'
import { selectPermission, userPrivileges } from 'api/User/selectors'
import { PERMISSIONS } from 'api/User/types'
import type { ChannelSummaryType } from 'ChannelSummary/types'
import classNames from 'classnames'
import { CircularChart } from 'components'
import CompletenessDetailsContainer from 'components/Completeness/CompletenessDetailsContainer'
import { openStoreDetails } from 'components/Store/actions'
import ChannelCarouselTable from 'CountriesList/ChannelCarouselTable'
import { selectStores, selectTabRequestParams } from 'data/selectors'
import type { DetailsData } from 'data/types'
import { getDateKey } from 'data/utils'
import { TypeAnalysis } from 'DatePickerDialog/constants'
import { DetailsToggleButtonGroup } from 'DetailsToggleButtonGroup'
import { LoadingOverlay } from 'LoadingOverlay'
import { moment } from 'locale'
import React, { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { changeDataSorting } from 'revenue/actions'
import RevenueDetailsTable from 'revenue/RevenueDetails/RevenueDetailsTable'
import type { State } from 'store/types'
import { useToggle } from 'utils'

import Box from '@mui/material/Box'

import ClubCardsDetails from './ClubCardsDetails'
import { ReplenishmentDetails } from './ReplenishmentDetails'
import StoresCountDetails from './StoresCountDetails'
import TrafficDetails from './TrafficDetails'
import useStyles from './useStyles'
import { getDefaultContentType, isValidTemperature } from './utils'

export interface GroupDetailsProps {
  data: DetailsData
  isLoading?: boolean
  channel?: string
  countryId?: string
  owner?: string
  saleType?: string
  type: ChannelSummaryType
}

export function GroupDetails({ channel, countryId, data, isLoading = false, owner, saleType, type }: GroupDetailsProps) {
  const { t: translate } = useTranslation()
  const dispatch = useDispatch()
  const isGroup = type === 'group'
  const showCompleteness = useSelector(selectPermission(PERMISSIONS.COMPLETENESS))
  const classes = useStyles()
  const { isToggled, toggle } = useToggle(false)
  const tabRequestParams = useSelector(selectTabRequestParams)
  const isListDetailsOpen = useSelector((state: State) => state.isListDetailsOpen)
  const initialContentType = getDefaultContentType({ data, type })
  const [contentType, setContentType] = useState<StoresType>(initialContentType)
  const countryData = data as CountryResponse
  const dataKey = getDateKey(
    { ...tabRequestParams, channel, countryId, isListDetailsOpen, storesType: 'stores', owner, saleType, type },
    isGroup,
  )
  const {
    isLoading: isLoadingStores,
    data: storesData,
    errorCode,
    sortBy,
    sortOrder,
  } = useSelector(selectStores({ channel, countryId, storesType: contentType, owner, saleType, type }))
  const replenishmentVisibility = useSelector((state: State) => state.user.settings.replenishmentVisibility)
  const dateCurrent = useSelector((state: State) => state.datePicker.dateCurrent)
  const isCumulative = useSelector((state: State) => state.datePicker.typeAnalysis !== TypeAnalysis.perDay)
  const userPrivilegesData = useSelector(userPrivileges)
  const dateComparedNECom = data.dateCompared ? moment(data.dateCompared).format('DD.MM.YYYY') : '-'
  const dateComparedECom = data.dateComparedECom && moment(data.dateComparedECom).format('DD.MM.YYYY')
  const showReplenishmentsTable = Boolean(replenishmentVisibility && countryData.anyReplenishments)
  const isDefaultContent = contentType === 'default'
  const showTemperature = isValidTemperature(data.temperatureCurrent) || isValidTemperature(data.temperatureCompared)
  const changeDataSortingAction = useCallback(
    args => {
      dispatch(
        changeDataSorting({
          ...tabRequestParams,
          ...args,
          channel,
          countryId,
          dateCurrent,
          isListDetailsOpen,
          owner,
          saleType,
          storesType: contentType,
          type,
        }),
      )
    },
    [dispatch, tabRequestParams, channel, countryId, dateCurrent, isListDetailsOpen, owner, saleType, contentType, type],
  )
  const fetchStoresAction = useCallback(() => {
    dispatch(
      fetchStores({
        ...tabRequestParams,
        isListDetailsOpen,
        channel,
        countryId,
        owner,
        saleType,
        storesType: contentType,
        type,
      }),
    )
  }, [dispatch, tabRequestParams, isListDetailsOpen, channel, countryId, owner, saleType, contentType, type])
  const onSetContentType = useCallback(
    (newContentType: StoresType) => {
      const nextContentType = newContentType === contentType ? 'default' : newContentType

      if (nextContentType !== 'default') {
        dispatch(
          fetchStores({
            ...tabRequestParams,
            isListDetailsOpen,
            channel,
            countryId,
            owner,
            saleType,
            storesType: nextContentType,
            type,
          }),
        )
      }
      setContentType(nextContentType)
    },
    [contentType, dispatch, tabRequestParams, isListDetailsOpen, channel, countryId, owner, saleType, type],
  )
  const fetchStoreDetails = (storeId: number) => {
    dispatch(
      fetchStore({
        dateCurrent,
        storeId,
      }),
    )
  }
  const openModal = (storeId: number, countryId: string) => {
    fetchStoreDetails(storeId)
    dispatch(openStoreDetails({ storeId, countryId }))
  }

  return (
    <LoadingOverlay isLoading={isLoading || isLoadingStores} overlayClassName={classes.loaderOverlay}>
      <Box padding="1.4rem 1.6rem 1.2rem 1.6rem">
        <Box alignItems="flex-start" display="flex" justifyContent="space-between">
          <Box>
            <DetailsToggleButtonGroup
              contentType={contentType}
              showStores={isGroup || data.anyStoresNECom}
              showWebsites={isGroup || data.anyStoresECom}
              onChange={onSetContentType}
            />
            <Box className={classes.comparedDate}>
              {translate('revenue.stats.comparableDay')} {dateComparedNECom}
              {dateComparedECom && dateComparedECom !== dateComparedNECom && (
                <span className="compared-date-ecom">
                  , <i className="fm fm-at" /> {dateComparedECom}
                </span>
              )}
            </Box>
          </Box>
          {isGroup && showCompleteness && (
            <>
              <Box flexBasis="25%">
                <CircularChart
                  label={translate('revenue.stats.completeness')}
                  openCompleteness={toggle}
                  values={[data.completeness, data.biCompleteness]}
                />
              </Box>
              <CompletenessDetailsContainer hideModal={toggle} isOpen={isToggled} />
            </>
          )}
        </Box>
        {isDefaultContent && (
          <>
            <StoresCountDetails data={data} />
            <Box alignItems="center" display="flex" justifyContent="space-between" mt={1}>
              <ReplenishmentDetails channel={channel} countryId={countryId} type={type} />
              {showTemperature && (
                <Box display="flex" alignItems="center" className={classes.temperature}>
                  <i className={classNames('fm fm-temperature', classes.temperatureIcon)} />
                  <Box alignItems="baseline" display="flex">
                    {isValidTemperature(data.temperatureCurrent) && (
                      <div className={classes.temperatureCurrent}>{data.temperatureCurrent}°C</div>
                    )}
                    {isValidTemperature(data.temperatureCompared) && `${translate('revenue.ly')} ${data.temperatureCompared}°C`}
                  </Box>
                </Box>
              )}
            </Box>
          </>
        )}
      </Box>
      {isDefaultContent && (
        <>
          <ClubCardsDetails data={data} type={type} />
          <TrafficDetails data={data} />
          <ChannelCarouselTable data={data} dataKey={dataKey} />
        </>
      )}
      {!isDefaultContent && (
        <RevenueDetailsTable
          changeDataSorting={changeDataSortingAction}
          contentType={contentType}
          errorCode={errorCode}
          fetchStoreDetails={fetchStoreDetails}
          fetchStores={fetchStoresAction}
          isCumulative={isCumulative || false}
          openModal={openModal}
          showClubCardTable={countryData.anyClubCards || isGroup}
          showOrders={(data.anyOrders && contentType === 'websites') || false}
          showReplenishmentsTable={showReplenishmentsTable || isGroup}
          sortBy={sortBy}
          sortOrder={sortOrder}
          stores={storesData}
          type={type}
          userPrivileges={userPrivilegesData}
        />
      )}
    </LoadingOverlay>
  )
}
